import React, { useState } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import { CommentInput } from './CommentInput';
import type { Comment } from '@/services/api';

/**
 * Renders comment text with @[Name](userId) mention markup as highlighted spans.
 * Plain text segments are rendered as-is; mentions become styled inline elements.
 */
export function renderCommentContent(text: string): React.ReactNode {
  const mentionRegex = /@\[([^\]]+)\]\([^)]+\)/g;
  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = mentionRegex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    parts.push(
      <span
        key={match.index}
        className="text-blue-600 font-medium bg-blue-50 px-0.5 rounded"
      >
        @{match[1]}
      </span>
    );
    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return parts.length > 0 ? <>{parts}</> : text;
}

interface CommentItemProps {
  comment: Comment;
  currentUserId: string | undefined;
  canComment: boolean;
  onReply: (parentId: string, content: string) => Promise<void>;
  onEdit: (commentId: string, content: string) => Promise<void>;
  onDelete: (commentId: string, hasReplies: boolean) => void;
  onPin?: (commentId: string) => Promise<void>;
  canPin?: boolean;
  isReply?: boolean;
  /** Skip rendering nested replies — used when the caller renders them separately (e.g. outside a pinned comment's highlight box). */
  hideReplies?: boolean;
}

export function CommentItem({
  comment,
  currentUserId,
  canComment,
  onReply,
  onEdit,
  onDelete,
  onPin,
  canPin,
  isReply = false,
  hideReplies = false,
}: CommentItemProps) {
  const [isReplying, setIsReplying] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  const isOwner = currentUserId === comment.userId;
  const hasReplies = comment.replies && comment.replies.length > 0;

  const handleReply = async (content: string) => {
    await onReply(comment.id, content);
    setIsReplying(false);
  };

  const handleEdit = async (content: string) => {
    await onEdit(comment.id, content);
    setIsEditing(false);
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const timeAgo = formatDistanceToNow(new Date(comment.createdAt), {
    addSuffix: true,
  });

  const wasEdited = comment.updatedAt !== comment.createdAt;

  return (
    <div className={`${isReply ? 'ml-10 mt-3' : ''}`}>
      <div className="flex gap-3">
        {/* Avatar */}
        <div
          className={`flex-shrink-0 ${
            isReply ? 'w-8 h-8' : 'w-10 h-10'
          } bg-blue-100 text-blue-600 rounded-full flex items-center justify-center font-medium ${
            isReply ? 'text-xs' : 'text-sm'
          }`}
        >
          {getInitials(comment.user.name)}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          {/* Header */}
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium text-gray-900 text-sm">
              {comment.user.name}
            </span>
            <span className="text-xs text-gray-500">{timeAgo}</span>
            {wasEdited && (
              <span className="text-xs text-gray-400">(edited)</span>
            )}
          </div>

          {/* Body */}
          {isEditing ? (
            <div className="mt-2">
              <CommentInput
                onSubmit={handleEdit}
                onCancel={() => setIsEditing(false)}
                initialValue={comment.content}
                buttonText="Save"
                autoFocus
              />
            </div>
          ) : (
            <div className="mt-1 text-sm text-gray-700 whitespace-pre-wrap break-words">
              {renderCommentContent(comment.content)}
            </div>
          )}

          {/* Actions */}
          {!isEditing && (
            <div className="flex items-center gap-4 mt-2">
              {/* Reply button - only for top-level comments, only if user can comment */}
              {!isReply && canComment && (
                <button
                  onClick={() => setIsReplying(!isReplying)}
                  className="text-xs text-gray-500 hover:text-blue-600 font-medium transition-colors"
                >
                  Reply
                </button>
              )}

              {/* Edit/Delete - only for owner */}
              {isOwner && (
                <>
                  <button
                    onClick={() => setIsEditing(true)}
                    className="flex items-center gap-1 text-xs text-gray-500 hover:text-blue-600 transition-colors"
                  >
                    <PencilIcon className="w-3 h-3" />
                    Edit
                  </button>
                  <button
                    onClick={() => onDelete(comment.id, hasReplies || false)}
                    className="flex items-center gap-1 text-xs text-gray-500 hover:text-red-600 transition-colors"
                  >
                    <TrashIcon className="w-3 h-3" />
                    Delete
                  </button>
                </>
              )}

              {/* Pin button - top-level comments only, admin/editor only */}
              {!isReply && canPin && onPin && (
                <button
                  onClick={() => onPin(comment.id)}
                  className={`flex items-center gap-1 text-xs transition-colors ${
                    comment.isPinned
                      ? 'text-amber-600 hover:text-amber-700 font-medium'
                      : 'text-gray-500 hover:text-amber-600'
                  }`}
                  title={comment.isPinned ? 'Unpin comment' : 'Pin comment'}
                >
                  {comment.isPinned ? (
                    // Solid pin icon (filled)
                    <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M16 12V4h1V2H7v2h1v8l-2 2v2h5.2v6h1.6v-6H18v-2l-2-2z" />
                    </svg>
                  ) : (
                    // Outline pin icon
                    <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M16 12V4h1V2H7v2h1v8l-2 2v2h5.2v6h1.6v-6H18v-2l-2-2z" />
                    </svg>
                  )}
                  {comment.isPinned ? 'Unpin' : 'Pin'}
                </button>
              )}
            </div>
          )}

          {/* Reply input */}
          {isReplying && (
            <div className="mt-3">
              <CommentInput
                onSubmit={handleReply}
                onCancel={() => setIsReplying(false)}
                placeholder={`Reply to ${comment.user.name}...`}
                buttonText="Reply"
                autoFocus
              />
            </div>
          )}
        </div>
      </div>

      {/* Nested replies */}
      {hasReplies && !hideReplies && (
        <div className="mt-3 border-l-2 border-gray-100 pl-3">
          {comment.replies!.map((reply) => (
            <CommentItem
              key={reply.id}
              comment={reply}
              currentUserId={currentUserId}
              canComment={canComment}
              onReply={onReply}
              onEdit={onEdit}
              onDelete={onDelete}
              onPin={onPin}
              canPin={canPin}
              isReply
            />
          ))}
        </div>
      )}
    </div>
  );
}
